import { useCallback, useEffect, useMemo, useState } from "react";
import { useLoading } from "shared/contexts/Loading";
import apiCall from "shared/services/ApiAdapter";

/**
 * Hook to fetch evaluations for a batch, with an optional courseId shortcut.
 * - If courseId is provided, fetch evaluations by courseId
 * - Otherwise, fetch assignments by batchId, collect unique evaluationIds and then fetch evaluations by those IDs
 */
const useBatchEvaluations = (batchId, courseId) => {
  const { startLoading, stopLoading } = useLoading();
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchBatchEvaluations = useCallback(async () => {
    if (!batchId && !courseId) {
      setEvaluations([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let evaluationsData = [];

      if (courseId) {
        // Direct by courseId (simpler)
        const data = await apiCall(
          `/evaluations?courseId.equals=${courseId}&size=1000`,
          false,
          "GET",
          null,
          startLoading,
          stopLoading,
          () => {}
        );
        evaluationsData = Array.isArray(data) ? data : data?.content ?? [];
      } else if (batchId) {
        // Through assignments (works without courseId)
        const assignmentsData = await apiCall(
          `/assignments?batchId.equals=${batchId}&size=1000`,
          false,
          "GET",
          null,
          startLoading,
          stopLoading,
          () => {}
        );
        const assignments = Array.isArray(assignmentsData)
          ? assignmentsData
          : assignmentsData?.content ?? [];
        const evaluationIds = Array.from(
          new Set(
            assignments
              .map((a) => a.evaluation?.id)
              .filter((id) => id != null)
          )
        );
        if (evaluationIds.length > 0) {
          // Build multiple id.equals filters (more compatible)
          const params = evaluationIds.map((id) => `id.equals=${encodeURIComponent(id)}`).join("&");
          const evalsData = await apiCall(
            `/evaluations?${params}&size=1000`,
            false,
            "GET",
            null,
            startLoading,
            stopLoading,
            () => {}
          );
          evaluationsData = Array.isArray(evalsData)
            ? evalsData
            : evalsData?.content ?? [];
        } else {
          evaluationsData = [];
        }
      }

      setEvaluations(evaluationsData);
    } catch (err) {
      console.error("Error fetching batch evaluations:", err);
      setError(err);
      setEvaluations([]);
    } finally {
      setLoading(false);
    }
  }, [batchId, courseId, startLoading, stopLoading]);

  useEffect(() => {
    fetchBatchEvaluations();
  }, [fetchBatchEvaluations]);

  return { evaluations, loading, error, refetch: fetchBatchEvaluations };
};

export default useBatchEvaluations;


